import { useState, useEffect } from 'react';
import type { Book, BookRecord, Shelf } from 'types';
import { bookRecordService } from 'services/bookRecordService';
import { shelfService } from 'services/shelfService';
import { getImageUrl } from 'utils/imageHelper';
import './BookDetailModal.css';

interface BookDetailModalProps {
  book: Book;
  onClose: () => void;
}

const HISTORY_ITEMS_PER_PAGE = 10;

export const BookDetailModal = ({ book, onClose }: BookDetailModalProps) => {
  const [records, setRecords] = useState<BookRecord[]>([]);
  const [shelf, setShelf] = useState<Shelf | null>(null);
  const [currentBook, setCurrentBook] = useState<Book>(book);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadBookRecords();
    loadShelf();
    loadCurrentBook();
  }, [book.id]);

  const loadBookRecords = async () => {
    try {
      setIsLoading(true);
      const data = await bookRecordService.getRecordsByBookId(book.id);
      setRecords(data);
      setError('');
    } catch (err) {
      console.error('Error loading book records:', err);
      setError('Failed to load history');
    } finally {
      setIsLoading(false);
    }
  };

  const loadCurrentBook = async () => {
    try {
      const bookServiceModule = await import('services/bookService');
      const updatedBook = await bookServiceModule.bookService.getBook(book.id);
      setCurrentBook(updatedBook);
      // Also reload shelf with updated shelf_id
      if (updatedBook.shelf_id) {
        const shelfData = await shelfService.getShelf(updatedBook.shelf_id);
        setShelf(shelfData);
      } else {
        setShelf(null);
      }
    } catch (err) {
      console.error('Error loading current book:', err);
      // Fallback to original book data
      setCurrentBook(book);
    }
  };

  const loadShelf = async () => {
    if (!book.shelf_id) {
      setShelf(null);
      return;
    }
    try {
      const shelfData = await shelfService.getShelf(book.shelf_id);
      setShelf(shelfData);
    } catch (err) {
      console.error('Error loading shelf:', err);
      setShelf(null);
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(records.length / HISTORY_ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * HISTORY_ITEMS_PER_PAGE;
  const endIndex = startIndex + HISTORY_ITEMS_PER_PAGE;
  const paginatedRecords = records.slice(startIndex, endIndex);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'borrowed':
        return 'status-borrowed';
      case 'returned':
        return 'status-returned';
      case 'lost':
        return 'status-lost';
      case 'added':
        return 'status-added';
      default:
        return '';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content book-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Book Details</h2>
          <button className="modal-close" onClick={onClose}>
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        <div className="modal-body book-detail-body">
          {/* Book Information Section */}
          <div className="book-info-section">
            <div className="book-cover-large">
              {getImageUrl(currentBook.image_path) ? (
                <img src={getImageUrl(currentBook.image_path) || ''} alt={currentBook.title} onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = 'none';
                  const placeholder = target.nextElementSibling as HTMLElement;
                  if (placeholder) {
                    placeholder.style.display = 'flex';
                  }
                }} />
              ) : null}
              <div className="book-cover-placeholder-large" style={{ display: getImageUrl(currentBook.image_path) ? 'none' : 'flex' }}>
                <div className="placeholder-large-content">
                  <span className="placeholder-large-title">{currentBook.title}</span>
                  <span className="placeholder-large-author">{currentBook.author_name}</span>
                </div>
              </div>
            </div>
            <div className="book-info-content">
              <h3 className="book-detail-title">{currentBook.title}</h3>
              <p className="book-detail-id">Book ID: {currentBook.book_id}</p>
              <div className="book-info-grid">
                <div className="info-item">
                  <span className="info-label">Author:</span>
                  <span className="info-value">{currentBook.author_name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">ISBN:</span>
                  <span className="info-value">{currentBook.isbn || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Publisher:</span>
                  <span className="info-value">{currentBook.publisher || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Publication Year:</span>
                  <span className="info-value">{currentBook.publication_year || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Category:</span>
                  <span className="info-value">{currentBook.category || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Shelf Number:</span>
                  <span className="info-value">{shelf ? shelf.shelf_number : 'Not assigned'}</span>
                </div>
              </div>
              {currentBook.description && (
                <div className="info-item description-item">
                  <span className="info-label">Description:</span>
                  <p className="info-description">{book.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* History Roadmap Section */}
          <div className="history-section">
            <h4 className="section-title">
              <span className="material-symbols-outlined">history</span>
              History
            </h4>

            {isLoading ? (
              <div className="loading-container">
                <span className="material-symbols-outlined loading-spinner">progress_activity</span>
                <p>Loading history...</p>
              </div>
            ) : error ? (
              <div className="alert alert-error">{error}</div>
            ) : records.length === 0 ? (
              <div className="empty-history">
                <span className="material-symbols-outlined">history_toggle_off</span>
                <p>No history available</p>
                <p className="empty-subtitle">This book has never been borrowed</p>
              </div>
            ) : (
              <div className="roadmap-container">
                <div className="roadmap">
                  {paginatedRecords.map((record, index) => {
                    const isSystemEntry = record.borrower_name === 'System';
                    const recordStatus = isSystemEntry ? 'added' : record.status;
                    return (
                    <div key={`${record.id}-${record.borrower_name}-${record.borrowed_date}`} className={`roadmap-item ${isSystemEntry ? 'system-entry' : ''}`}>
                      <div className="roadmap-marker">
                        <span className={`marker-dot ${isSystemEntry ? 'status-added' : getStatusBadgeClass(record.status)}`}></span>
                        {index !== records.length - 1 && <div className="marker-line"></div>}
                      </div>
                      <div className="roadmap-content">
                        <div className="record-card">
                          <div className="record-header">
                            <div className="record-borrower">
                              <span className="material-symbols-outlined">{isSystemEntry ? 'admin_panel_settings' : 'person'}</span>
                              <span className="borrower-name">{record.borrower_name}</span>
                            </div>
                            <span className={`status-badge ${getStatusBadgeClass(recordStatus)}`}>
                              {isSystemEntry ? 'Added' : getStatusLabel(record.status)}
                            </span>
                          </div>

                          <div className="record-dates">
                            <div className="date-item">
                              <span className="material-symbols-outlined">{isSystemEntry ? 'library_add' : 'calendar_today'}</span>
                              <span className="date-label">{isSystemEntry ? 'Added:' : 'Borrowed:'}</span>
                              <span className="date-value">{formatDate(record.borrowed_date)}</span>
                            </div>
                            {record.return_date && (
                              <div className="date-item">
                                <span className="material-symbols-outlined">event_available</span>
                                <span className="date-label">Returned:</span>
                                <span className="date-value">{formatDate(record.return_date)}</span>
                              </div>
                            )}
                          </div>

                          {record.contact_info && (
                            <div className="record-contact">
                              <span className="material-symbols-outlined">contact_phone</span>
                              <span>{record.contact_info}</span>
                            </div>
                          )}

                          {record.notes && (
                            <div className="record-notes">
                              <span className="material-symbols-outlined">note</span>
                              <p>{record.notes}</p>
                            </div>
                          )}

                          {record.late_fees && parseFloat(record.late_fees) > 0 && (
                            <div className="record-late-fees">
                              <span className="material-symbols-outlined">payments</span>
                              <span>Late Fees: ${parseFloat(record.late_fees).toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="history-pagination">
                    <button 
                      className="btn btn-ghost"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <span className="material-symbols-outlined">chevron_left</span>
                      Previous
                    </button>
                    <span className="pagination-info">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button 
                      className="btn btn-ghost"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <span className="material-symbols-outlined">chevron_right</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
